import { useEffect, useId, useRef, useState } from "react";
import { ApiValidationError, StaleWriteError } from "./api";
import {
    type DraftExpression,
    type DraftGroup,
    emptyExpression,
    emptyGroup,
    hasBlockingProblems,
    MAX_GROUP_LEVELS,
    operatorsFor,
    removeGroup,
    snapOperator,
    toDraft,
    toPayload,
    treeWarnings,
    updateGroup,
} from "./conditionTree";
import type { Condition, EditorMetadata, LogicalOperator } from "./types";

/**
 * Editor for a condition's group and expression tree.
 *
 * The whole tree is one local draft saved in a single request: the API replaces the tree
 * wholesale, and a nested structure is exactly where incremental client-side diffing would go
 * wrong. Optimistic concurrency is on the parent condition's updated_at, so a save against a
 * stale copy is refused rather than silently overwriting someone else's tree.
 */

function ExpressionRow({
    expression,
    metadata,
    onChange,
    onRemove,
}: {
    expression: DraftExpression;
    metadata: EditorMetadata;
    onChange: (next: DraftExpression) => void;
    onRemove: () => void;
}) {
    const idPrefix = useId();
    const operators = operatorsFor(metadata, expression.fieldType);
    const fieldNames = metadata.fieldNames[expression.fieldType] ?? [];

    return (
        <li className="aer-wf-expr">
            <div className="aer-wf-expr__row">
                <div className="aer-wf-field">
                    <label htmlFor={`${idPrefix}-type`}>Field type</label>
                    <select
                        id={`${idPrefix}-type`}
                        value={expression.fieldType}
                        onChange={(event) => {
                            const fieldType = event.target.value;
                            onChange({
                                ...expression,
                                fieldType: fieldType,
                                // Snapped rather than left illegal: extra_data supports only
                                // eq/neq, and the evaluator raises on anything else at runtime.
                                operator: snapOperator(
                                    metadata,
                                    fieldType,
                                    expression.operator,
                                ),
                                // The field list is per type, so a carried-over name would not
                                // exist on the new type.
                                fieldName: "",
                            });
                        }}
                    >
                        {metadata.fieldTypes.map((entry) => (
                            <option key={entry.value} value={entry.value}>
                                {entry.value}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="aer-wf-field">
                    <label htmlFor={`${idPrefix}-name`}>Field</label>
                    <select
                        id={`${idPrefix}-name`}
                        value={expression.fieldName}
                        onChange={(event) =>
                            onChange({
                                ...expression,
                                fieldName: event.target.value,
                            })
                        }
                    >
                        <option value="">— choose —</option>
                        {fieldNames.map((name) => (
                            <option key={name} value={name}>
                                {name}
                            </option>
                        ))}
                    </select>
                    {fieldNames.length === 0 && (
                        <p className="aer-wf-field__hint aer-wf-field__hint--warn">
                            No {expression.fieldType} fields exist for this
                            company's data.
                        </p>
                    )}
                </div>

                <div className="aer-wf-field">
                    <label htmlFor={`${idPrefix}-op`}>Operator</label>
                    <select
                        id={`${idPrefix}-op`}
                        value={expression.operator}
                        onChange={(event) =>
                            onChange({
                                ...expression,
                                operator: event.target.value,
                            })
                        }
                    >
                        {operators.map((operator) => (
                            <option key={operator} value={operator}>
                                {operator}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="aer-wf-field">
                    <label htmlFor={`${idPrefix}-value`}>Value</label>
                    <input
                        id={`${idPrefix}-value`}
                        type="text"
                        value={expression.value}
                        onChange={(event) =>
                            onChange({
                                ...expression,
                                value: event.target.value,
                            })
                        }
                    />
                </div>

                {expression.operator === "between" && (
                    <div className="aer-wf-field">
                        <label htmlFor={`${idPrefix}-value2`}>and</label>
                        <input
                            id={`${idPrefix}-value2`}
                            type="text"
                            value={expression.value2}
                            onChange={(event) =>
                                onChange({
                                    ...expression,
                                    value2: event.target.value,
                                })
                            }
                        />
                    </div>
                )}

                <button
                    type="button"
                    className="aer-wf-icon-button aer-wf-icon-button--danger"
                    aria-label="Remove expression"
                    onClick={onRemove}
                >
                    ✕
                </button>
            </div>
        </li>
    );
}

function GroupEditor({
    group,
    metadata,
    depth,
    isRoot,
    warningKeys,
    onChange,
    onRemove,
}: {
    group: DraftGroup;
    metadata: EditorMetadata;
    depth: number;
    isRoot: boolean;
    warningKeys: Set<string>;
    onChange: (update: (group: DraftGroup) => DraftGroup) => void;
    onRemove: () => void;
}) {
    const idPrefix = useId();
    const flagged = warningKeys.has(group.key);

    return (
        <li
            className={`aer-wf-group${flagged ? " aer-wf-group--flagged" : ""}`}
            style={{ marginLeft: depth ? "0.75rem" : undefined }}
        >
            <div className="aer-wf-group__header">
                <div className="aer-wf-field aer-wf-field--inline">
                    <label htmlFor={`${idPrefix}-op`}>Combine with</label>
                    <select
                        id={`${idPrefix}-op`}
                        value={group.logicalOperator}
                        onChange={(event) =>
                            onChange((current) => ({
                                ...current,
                                logicalOperator: event.target
                                    .value as LogicalOperator,
                            }))
                        }
                    >
                        {metadata.logicalOperators.map((operator) => (
                            <option key={operator} value={operator}>
                                {operator}
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    type="button"
                    className="aer-wf-icon-button aer-wf-icon-button--danger"
                    aria-label={isRoot ? "Remove group" : "Remove sub-group"}
                    onClick={onRemove}
                >
                    ✕
                </button>
            </div>

            <div className="aer-wf-fields aer-wf-fields--row">
                <div className="aer-wf-field">
                    <label htmlFor={`${idPrefix}-geo`}>Geo area</label>
                    <select
                        id={`${idPrefix}-geo`}
                        value={
                            group.geoArea === null ? "" : String(group.geoArea)
                        }
                        onChange={(event) =>
                            onChange((current) => ({
                                ...current,
                                geoArea: event.target.value || null,
                            }))
                        }
                    >
                        <option value="">Anywhere</option>
                        {metadata.geoAreas.map((area) => (
                            <option key={area.value} value={area.value}>
                                {area.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="aer-wf-field">
                    <label htmlFor={`${idPrefix}-providers`}>
                        Data providers
                    </label>
                    <select
                        id={`${idPrefix}-providers`}
                        multiple
                        size={3}
                        value={group.dataProviders}
                        onChange={(event) =>
                            onChange((current) => ({
                                ...current,
                                dataProviders: [
                                    ...event.target.selectedOptions,
                                ].map((option) => option.value),
                            }))
                        }
                    >
                        {metadata.dataProviders.map((provider) => (
                            <option key={provider.value} value={provider.value}>
                                {provider.label}
                            </option>
                        ))}
                    </select>
                    <p className="aer-wf-field__hint">
                        Leave empty for all providers.
                    </p>
                </div>
            </div>

            <ul className="aer-wf-exprs">
                {group.expressions.map((expression, index) => (
                    <ExpressionRow
                        key={expression.key}
                        expression={expression}
                        metadata={metadata}
                        onChange={(next) =>
                            onChange((current) => ({
                                ...current,
                                expressions: current.expressions.map(
                                    (item, i) => (i === index ? next : item),
                                ),
                            }))
                        }
                        onRemove={() =>
                            onChange((current) => ({
                                ...current,
                                expressions: current.expressions.filter(
                                    (_, i) => i !== index,
                                ),
                            }))
                        }
                    />
                ))}
            </ul>

            <div className="aer-wf-group__actions">
                <button
                    type="button"
                    className="aer-wf-button aer-wf-button--secondary"
                    onClick={() =>
                        onChange((current) => ({
                            ...current,
                            expressions: [
                                ...current.expressions,
                                emptyExpression(),
                            ],
                        }))
                    }
                >
                    Add expression
                </button>
                {depth + 1 < MAX_GROUP_LEVELS && (
                    <button
                        type="button"
                        className="aer-wf-button aer-wf-button--secondary"
                        onClick={() =>
                            onChange((current) => ({
                                ...current,
                                children: [...current.children, emptyGroup()],
                            }))
                        }
                    >
                        Add sub-group
                    </button>
                )}
            </div>

            {group.children.length > 0 && (
                <ul className="aer-wf-groups">
                    {group.children.map((child) => (
                        <GroupEditor
                            key={child.key}
                            group={child}
                            metadata={metadata}
                            depth={depth + 1}
                            isRoot={false}
                            warningKeys={warningKeys}
                            onChange={(update) =>
                                onChange((current) => ({
                                    ...current,
                                    children: updateGroup(
                                        current.children,
                                        child.key,
                                        update,
                                    ),
                                }))
                            }
                            onRemove={() =>
                                onChange((current) => ({
                                    ...current,
                                    children: removeGroup(
                                        current.children,
                                        child.key,
                                    ),
                                }))
                            }
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}

export function ConditionBuilder({
    condition,
    metadata,
    isSaving,
    saveError,
    onDirtyChange,
    onSave,
}: {
    condition: Condition;
    metadata: EditorMetadata;
    isSaving: boolean;
    saveError: unknown;
    onDirtyChange: (dirty: boolean) => void;
    onSave: (payload: {
        groups: unknown[];
        expectedVersion: string;
        acknowledgeWarnings: boolean;
    }) => void;
}) {
    const [groups, setGroups] = useState<DraftGroup[]>(() =>
        toDraft(condition),
    );
    const [baseline, setBaseline] = useState(() =>
        JSON.stringify(toPayload(toDraft(condition))),
    );

    // A successful save returns a fresh condition with new ids; adopting it keeps the draft from
    // diverging and resets the concurrency token.
    useEffect(() => {
        const draft = toDraft(condition);
        setGroups(draft);
        setBaseline(JSON.stringify(toPayload(draft)));
    }, [condition]);

    const isDirty = JSON.stringify(toPayload(groups)) !== baseline;

    /**
     * Report the draft state without depending on the callback's identity.
     *
     * Depending on it caused an endless render loop. Parents pass an inline arrow, so the
     * identity changes every render; the effect re-ran, its cleanup reported clean and its body
     * reported dirty, each flipping state in the owner, which re-rendered, which produced
     * another new arrow. In the browser that never settled — the builder re-rendered
     * continuously and a click on the multi-select was clobbered before it could stick. In jsdom
     * it happened to converge after a few renders, which is why the unit tests missed it.
     */
    const reportDirty = useRef(onDirtyChange);
    reportDirty.current = onDirtyChange;

    useEffect(() => {
        reportDirty.current(isDirty);
    }, [isDirty]);

    // Clearing on unmount is separate: it must happen once, when the builder goes away, not
    // every time the dirty flag flips.
    useEffect(() => () => reportDirty.current(false), []);

    const warnings = treeWarnings(groups);
    const blocked = hasBlockingProblems(groups);
    const warningKeys = new Set(
        warnings
            .map((warning) => warning.groupKey)
            .filter((key): key is string => key !== null),
    );

    // The server refuses an unusable tree unless the client acknowledges it; this is that
    // acknowledgement, and it resets whenever the draft changes so it cannot be carried over.
    const acknowledgementRequired =
        saveError instanceof ApiValidationError &&
        saveError.acknowledgementRequired;
    const serverWarnings =
        saveError instanceof ApiValidationError ? saveError.warnings : [];
    const fieldErrors =
        saveError instanceof ApiValidationError ? saveError.fieldErrors : {};
    const isStale = saveError instanceof StaleWriteError;

    return (
        <div className="aer-wf-builder">
            <p className="aer-wf-field__hint">
                Groups at the top level are combined with <strong>OR</strong>.
                Inside a group, its own operator applies.
            </p>

            {isStale && (
                <div className="aer-wf-notice aer-wf-notice--warn" role="alert">
                    <p>
                        This condition changed since it was loaded. Reload the
                        definition and reapply your changes — saving now would
                        overwrite someone else's edit.
                    </p>
                </div>
            )}

            {warnings.length > 0 && (
                <div className="aer-wf-notice aer-wf-notice--warn">
                    <ul>
                        {warnings.map((warning) => (
                            <li key={`${warning.groupKey}-${warning.message}`}>
                                {warning.message}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {Object.entries(fieldErrors).length > 0 && (
                <div
                    className="aer-wf-notice aer-wf-notice--danger"
                    role="alert"
                >
                    <ul>
                        {Object.entries(fieldErrors).map(
                            ([field, messages]) => (
                                <li key={field}>
                                    {field}: {messages.join(" ")}
                                </li>
                            ),
                        )}
                    </ul>
                </div>
            )}

            <ul className="aer-wf-groups">
                {groups.map((group) => (
                    <GroupEditor
                        key={group.key}
                        group={group}
                        metadata={metadata}
                        depth={0}
                        isRoot
                        warningKeys={warningKeys}
                        onChange={(update) =>
                            setGroups((current) =>
                                updateGroup(current, group.key, update),
                            )
                        }
                        onRemove={() =>
                            setGroups((current) =>
                                removeGroup(current, group.key),
                            )
                        }
                    />
                ))}
            </ul>

            <div className="aer-wf-rule__save">
                <button
                    type="button"
                    className="aer-wf-button aer-wf-button--secondary"
                    onClick={() =>
                        setGroups((current) => [...current, emptyGroup()])
                    }
                >
                    Add group
                </button>
                <button
                    type="button"
                    className="aer-wf-button"
                    disabled={!isDirty || blocked || isSaving}
                    onClick={() =>
                        onSave({
                            groups: toPayload(groups),
                            expectedVersion: condition.version,
                            acknowledgeWarnings: acknowledgementRequired,
                        })
                    }
                >
                    {isSaving
                        ? "Saving…"
                        : acknowledgementRequired
                          ? "Save anyway"
                          : "Save condition"}
                </button>
                {isDirty && <span className="aer-wf-rule__dirty">Unsaved</span>}
            </div>

            {acknowledgementRequired && (
                <div className="aer-wf-notice aer-wf-notice--warn" role="alert">
                    <p>
                        <strong>
                            This condition cannot fire as configured.
                        </strong>{" "}
                        Save anyway only if you intend to finish it later —
                        nothing using it will run in the meantime.
                    </p>
                    <ul>
                        {serverWarnings.map((warning) => (
                            <li key={warning}>{warning}</li>
                        ))}
                    </ul>
                </div>
            )}

            {blocked && (
                <p className="aer-wf-field__hint aer-wf-field__hint--warn">
                    Every expression needs a field and a value before this can
                    be saved.
                </p>
            )}
        </div>
    );
}
